import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

/** Which mini-preview glyph a dashboard card shows — picked from the FIRST
 *  chart widget found in its layout_json (see toAvailableViews). Illustrative
 *  shapes, not live data: the rail must render instantly for every dashboard
 *  without fetching each one's data. */
export type AvailableViewGlyph = 'bubble' | 'bar' | 'hbar' | 'line' | 'table' | 'kpi' | 'dashboard';

export interface AvailableView {
  /** Display name — `layout_json['dashboard-name']` (what the creator typed). */
  name: string;
  /** Route slug for `/dvm-published/<slug>`. */
  slug: string;
  glyph: AvailableViewGlyph;
}

const COLLAPSED_STORAGE_KEY = 'dvm.publishedRail.collapsed';

/** Map the email-filtered `dashboard_layouts/dynamic_filter` rows (as returned
 *  by SelfServiceDashboardService.fetchOwnedDashboardsFromApi) to rail cards.
 *  Publish is an append-insert, so the same slug shows up once per version —
 *  the LAST row wins (append order = most recent), matching the sidenav's
 *  last-write-wins cache. Landing pages are skipped: they're entry pages, not
 *  "views" to flip between. */
export function toAvailableViews(
  rows: Array<{ name: string; slug: string; layoutJson: Record<string, unknown> }>,
): AvailableView[] {
  const bySlug = new Map<string, AvailableView>();
  for (const row of rows || []) {
    const layout = row?.layoutJson;
    if (!layout || typeof layout !== 'object') continue;
    if (String((layout as any)['dashboardType'] ?? '') === 'landing-page') continue;
    const slug = String(row.slug || '').trim();
    if (!slug) continue;
    const name = String((layout as any)['dashboard-name'] ?? (layout as any)['name'] ?? '').trim() || row.name;
    bySlug.set(slug, { name, slug, glyph: pickGlyph((layout as any)['pageContent']) });
  }
  return Array.from(bySlug.values());
}

/** First chart-ish widget type in the page, depth-first — same walk shape as
 *  the published component's extractWidgets (nodes with a `type`, group-card
 *  `children`, arrays / nested objects). KPI-only dashboards get the kpi glyph. */
export function pickGlyph(pageContent: unknown): AvailableViewGlyph {
  let kpiSeen = false;
  let tableSeen = false;
  const visit = (node: any): AvailableViewGlyph | null => {
    if (!node || typeof node !== 'object') return null;
    if (Array.isArray(node)) {
      for (const child of node) {
        const found = visit(child);
        if (found) return found;
      }
      return null;
    }
    const type = String(node.type ?? '').toLowerCase();
    if (type) {
      const caps = (node.capabilities ?? {}) as Record<string, unknown>;
      switch (type) {
        case 'scatter-bubble':
          return 'bubble';
        case 'bar-column':
          return String(caps['orientation'] ?? caps['barOrientation'] ?? '').toLowerCase().startsWith('h') ? 'hbar' : 'bar';
        case 'horizontal-bar-chart':
          return 'hbar';
        case 'line-area':
          return 'line';
        case 'waterfall-chart':
          return 'bar';
        case 'table-matrix':
        case 'drill-down':
        case 'nested-table':
          tableSeen = true;
          break;
        case 'kpi-card':
          kpiSeen = true;
          break;
        case 'group-card':
          return visit(node.children);
        default:
          break;
      }
      return null;
    }
    for (const value of Object.values(node)) {
      const found = visit(value);
      if (found) return found;
    }
    return null;
  };
  return visit(pageContent) ?? (tableSeen ? 'table' : kpiSeen ? 'kpi' : 'dashboard');
}

/**
 * "Available Views" rail — the right-hand column of the published dashboard
 * listing the user's OTHER published dashboards as preview cards (title, arrow,
 * chart glyph). Clicking a card emits `open` with the slug; the parent routes.
 * Collapsible to a 56px strip; the choice is remembered per browser.
 */
@Component({
  selector: 'app-dvm-available-views-rail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './available-views-rail.component.html',
  styleUrl: './available-views-rail.component.scss',
})
export class DvmAvailableViewsRailComponent {
  @Input() views: AvailableView[] = [];
  /** The dashboard currently on screen — excluded from the list. */
  @Input() currentSlug = '';
  @Output() open = new EventEmitter<string>();

  @HostBinding('class.collapsed') collapsed = readCollapsed();

  get visibleViews(): AvailableView[] {
    const current = (this.currentSlug || '').trim().toLowerCase();
    return (this.views || []).filter((v) => v.slug.toLowerCase() !== current);
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, this.collapsed ? '1' : '0');
    } catch { /* storage unavailable — the toggle still works for this page */ }
  }

  openView(slug: string): void {
    if (slug) this.open.emit(slug);
  }

  trackBySlug(_: number, view: AvailableView): string {
    return view.slug;
  }
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
